import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import {
  Accordion, AccordionContent, AccordionItem, AccordionTrigger,
} from '@/components/ui/accordion';
import { DemoModal } from '@/components/DemoModal';
import {
  ArrowRight, Upload, Sparkles, FileText, Wand2, Layers, Library, Zap,
  CheckCircle2, Clock, Loader2, Twitter, Linkedin, Instagram, Youtube,
  ChevronRight, Play, Quote, Star, MoveRight, Workflow, Mic, Languages,
  ShieldCheck, Rocket, Brain, Music2, Hash,
} from 'lucide-react';

/* ---------- Reusable bits ---------- */

const BrowserFrame = ({ title = 'clipparrot.app', children, className = '' }) => (
  <div className={`relative rounded-2xl overflow-hidden border border-white/10 bg-zinc-950/90 shadow-[0_30px_80px_-20px_rgba(99,102,241,0.35)] ${className}`}>
    <div className="flex items-center gap-2 px-4 py-3 border-b border-white/5 bg-zinc-900/80">
      <span className="w-2.5 h-2.5 rounded-full bg-red-500/70" />
      <span className="w-2.5 h-2.5 rounded-full bg-yellow-500/70" />
      <span className="w-2.5 h-2.5 rounded-full bg-emerald-500/70" />
      <span className="ml-3 text-[11px] text-zinc-500 font-mono">{title}</span>
    </div>
    <div className="p-5 sm:p-6">{children}</div>
  </div>
);

const Glow = ({ className = '' }) => (
  <div className={`absolute pointer-events-none blur-3xl opacity-40 ${className}`} />
);

/* ---------- Hero mockup (dashboard preview) ---------- */

const HeroDashboardMock = () => (
  <BrowserFrame title="clipparrot.app/dashboard">
    <div className="grid grid-cols-12 gap-4">
      {/* Mini sidebar */}
      <div className="col-span-3 hidden sm:flex flex-col gap-1.5 border-r border-white/5 pr-3">
        {['Dashboard', 'Upload', 'Library', 'Pipeline', 'Presets'].map((l, i) => (
          <div key={l} className={`flex items-center gap-2 px-2.5 py-1.5 rounded-md text-[11px] ${i === 0 ? 'bg-indigo-500/15 text-indigo-300 border border-indigo-500/30' : 'text-zinc-500'}`}>
            <span className={`w-1.5 h-1.5 rounded-full ${i === 0 ? 'bg-indigo-400' : 'bg-zinc-600'}`} />
            {l}
          </div>
        ))}
      </div>

      {/* Main */}
      <div className="col-span-12 sm:col-span-9 space-y-4">
        {/* Stats row */}
        <div className="grid grid-cols-3 gap-3">
          {[
            { label: 'Clips processed', value: '128', tint: 'from-indigo-500/20' },
            { label: 'Posts generated', value: '412', tint: 'from-purple-500/20' },
            { label: 'Avg. time saved', value: '6.4h', tint: 'from-emerald-500/20' },
          ].map((s) => (
            <div key={s.label} className={`rounded-xl border border-white/5 bg-gradient-to-br ${s.tint} to-transparent p-3`}>
              <div className="text-[10px] text-zinc-500 uppercase tracking-wider">{s.label}</div>
              <div className="font-heading text-2xl text-zinc-100 mt-1">{s.value}</div>
            </div>
          ))}
        </div>

        {/* Pipeline rows */}
        <div className="space-y-2">
          {[
            { name: 'product_demo_v3.mp4', status: 'Ready', icon: CheckCircle2, badge: 'bg-emerald-500/10 text-emerald-300 border-emerald-500/20' },
            { name: 'founder_clip.mov', status: 'Transcribing', icon: Loader2, spin: true, badge: 'bg-blue-500/10 text-blue-300 border-blue-500/20' },
            { name: 'launch_teaser.mp4', status: 'Generating', icon: Sparkles, badge: 'bg-indigo-500/10 text-indigo-300 border-indigo-500/20' },
            { name: 'webinar_cut_42.mp4', status: 'Queued', icon: Clock, badge: 'bg-zinc-500/10 text-zinc-300 border-zinc-500/20' },
          ].map((row) => {
            const Icon = row.icon;
            return (
              <div key={row.name} className="flex items-center justify-between rounded-lg border border-white/5 bg-zinc-900/60 px-3 py-2.5">
                <div className="flex items-center gap-3 min-w-0">
                  <div className="w-7 h-7 rounded-md bg-zinc-800 flex items-center justify-center shrink-0">
                    <Play className="w-3 h-3 text-zinc-400" />
                  </div>
                  <span className="text-[12px] text-zinc-300 truncate">{row.name}</span>
                </div>
                <div className={`flex items-center gap-1.5 text-[10px] px-2 py-1 rounded-full border ${row.badge}`}>
                  <Icon className={`w-3 h-3 ${row.spin ? 'animate-spin' : ''}`} />
                  {row.status}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  </BrowserFrame>
);

/* ---------- Feature mockups ---------- */

const PresetBuilderMock = () => (
  <BrowserFrame title="clipparrot.app/presets/builder">
    <div className="space-y-3">
      <div className="flex items-center gap-2 text-[11px] text-zinc-500">
        <Wand2 className="w-3.5 h-3.5 text-indigo-400" /> AI Preset Builder
      </div>
      <div className="rounded-lg border border-white/5 bg-zinc-900/60 p-3 text-[12px] text-zinc-300">
        <span className="text-zinc-500">Describe your vibe:</span><br />
        "Punchy LinkedIn hook, founder voice, single bold takeaway, no emojis."
      </div>
      <div className="grid grid-cols-2 gap-2 text-[11px]">
        {[
          { k: 'Platform', v: 'LinkedIn' },
          { k: 'Tone', v: 'Bold, founder' },
          { k: 'Length', v: '90–140 words' },
          { k: 'Hashtags', v: 'Off' },
          { k: 'CTA', v: 'Soft prompt' },
          { k: 'Hook style', v: 'Contrarian' },
        ].map((p) => (
          <div key={p.k} className="rounded-md border border-white/5 bg-zinc-950/70 px-2.5 py-1.5">
            <div className="text-zinc-500 text-[10px] uppercase tracking-wider">{p.k}</div>
            <div className="text-zinc-200">{p.v}</div>
          </div>
        ))}
      </div>
      <div className="flex items-center justify-between pt-1">
        <div className="text-[10px] text-zinc-500">3 community presets remixed</div>
        <div className="inline-flex items-center gap-1.5 text-[11px] px-2.5 py-1 rounded-full bg-indigo-500/20 text-indigo-300 border border-indigo-500/30">
          <Sparkles className="w-3 h-3" /> Save preset
        </div>
      </div>
    </div>
  </BrowserFrame>
);

const PipelineMock = () => (
  <BrowserFrame title="clipparrot.app/pipeline">
    <div className="grid grid-cols-4 gap-2">
      {[
        { col: 'Uploaded', count: 3, tint: 'border-zinc-500/20 bg-zinc-500/5' },
        { col: 'Transcribing', count: 2, tint: 'border-blue-500/20 bg-blue-500/5' },
        { col: 'Generating', count: 1, tint: 'border-indigo-500/20 bg-indigo-500/5' },
        { col: 'Ready', count: 5, tint: 'border-emerald-500/20 bg-emerald-500/5' },
      ].map((c) => (
        <div key={c.col} className="space-y-2">
          <div className="flex items-center justify-between text-[10px] text-zinc-500 uppercase tracking-wider">
            <span>{c.col}</span>
            <span className="text-zinc-400">{c.count}</span>
          </div>
          {Array.from({ length: c.count > 3 ? 3 : c.count }).map((_, i) => (
            <div key={i} className={`rounded-md border p-2 ${c.tint}`}>
              <div className="h-1.5 w-3/4 bg-zinc-700 rounded mb-1.5" />
              <div className="h-1 w-1/2 bg-zinc-800 rounded" />
            </div>
          ))}
        </div>
      ))}
    </div>
  </BrowserFrame>
);

const LibraryMock = () => (
  <BrowserFrame title="clipparrot.app/library">
    <div className="flex gap-2 mb-3">
      {['All clips', 'Launch week', 'Founder cuts', 'Tutorials'].map((t, i) => (
        <div key={t} className={`text-[10px] px-2.5 py-1 rounded-full border ${i === 1 ? 'border-indigo-500/40 bg-indigo-500/10 text-indigo-300' : 'border-white/10 text-zinc-500'}`}>{t}</div>
      ))}
    </div>
    <div className="grid grid-cols-3 gap-2">
      {Array.from({ length: 6 }).map((_, i) => (
        <div key={i} className="rounded-md border border-white/5 bg-zinc-900/60 overflow-hidden">
          <div className="aspect-video bg-gradient-to-br from-indigo-500/20 via-purple-500/10 to-zinc-900 flex items-center justify-center">
            <Play className="w-4 h-4 text-white/60" />
          </div>
          <div className="p-1.5">
            <div className="h-1.5 w-3/4 bg-zinc-700 rounded mb-1" />
            <div className="h-1 w-1/2 bg-zinc-800 rounded" />
          </div>
        </div>
      ))}
    </div>
  </BrowserFrame>
);

const CopyOutputMock = () => (
  <BrowserFrame title="clipparrot.app/generate/launch-teaser/linkedin">
    <div className="grid grid-cols-2 gap-3">
      {[
        {
          tag: 'LinkedIn · Founder',
          body: 'Most teams ship features. Great teams ship outcomes. We rebuilt our onboarding around one metric — and watched activation jump 41% in 14 days. Here\'s what we cut...',
        },
        {
          tag: 'X / Twitter · Punchy',
          body: 'Cut your onboarding in half. Activation went up 41%. The lesson: every extra step is a tax on belief.',
        },
        {
          tag: 'Instagram · Caption',
          body: 'Built. Cut. Rebuilt. Activation up 41% — and we removed 6 steps doing it. Save this if you\'re mid-launch ↓',
        },
        {
          tag: 'YouTube · Description',
          body: 'In this clip: how we redesigned onboarding around one north-star metric, what we removed, and the 41% activation lift it produced.',
        },
      ].map((c) => (
        <div key={c.tag} className="rounded-lg border border-white/5 bg-zinc-900/60 p-3">
          <div className="text-[10px] uppercase tracking-wider text-indigo-300 mb-1.5">{c.tag}</div>
          <div className="text-[11px] text-zinc-300 leading-relaxed">{c.body}</div>
        </div>
      ))}
    </div>
  </BrowserFrame>
);

/* ---------- Page ---------- */

export default function LandingPage() {
  const navigate = useNavigate();
  const [scrolled, setScrolled] = useState(false);
  const [demoOpen, setDemoOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const goSignup = () => navigate('/login?mode=register');
  const goSignin = () => navigate('/login');

  return (
    <div className="min-h-screen bg-background text-foreground overflow-x-hidden" data-testid="landing-page">
      {/* Background atmosphere */}
      <div className="fixed inset-0 -z-10">
        <Glow className="top-[-10%] left-[-10%] w-[55vw] h-[55vw] bg-indigo-600/30" />
        <Glow className="top-[20%] right-[-15%] w-[60vw] h-[60vw] bg-purple-600/20" />
        <Glow className="bottom-[-10%] left-[20%] w-[50vw] h-[50vw] bg-fuchsia-700/15" />
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_-20%,rgba(99,102,241,0.18),transparent_60%)]" />
        <div
          className="absolute inset-0 opacity-[0.04] mix-blend-overlay"
          style={{
            backgroundImage:
              "url(\"data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='3' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)'/%3E%3C/svg%3E\")",
          }}
        />
      </div>

      {/* NAV */}
      <header
        className={`sticky top-0 z-50 transition-all duration-300 ${
          scrolled ? 'bg-zinc-950/80 backdrop-blur-xl border-b border-white/5' : 'bg-transparent'
        }`}
        data-testid="landing-nav"
      >
        <div className="max-w-7xl mx-auto flex items-center justify-between px-5 sm:px-8 py-4">
          <Link to="/" className="flex items-center gap-2" data-testid="landing-logo">
            <img src="/logo-white-wide.png" alt="ClipParrot" className="h-9 sm:h-10 object-contain" />
          </Link>
          <nav className="hidden md:flex items-center gap-8 text-sm text-zinc-400">
            <a href="#features" className="hover:text-white transition-colors" data-testid="nav-features">Features</a>
            <a href="#how-it-works" className="hover:text-white transition-colors" data-testid="nav-how">How it works</a>
            <a href="#use-cases" className="hover:text-white transition-colors" data-testid="nav-use-cases">Use cases</a>
            <a href="#pricing" className="hover:text-white transition-colors" data-testid="nav-pricing">Pricing</a>
            <a href="#faq" className="hover:text-white transition-colors" data-testid="nav-faq">FAQ</a>
          </nav>
          <div className="flex items-center gap-2 sm:gap-3">
            <button
              onClick={goSignin}
              className="hidden sm:inline-flex text-sm text-zinc-300 hover:text-white px-3 py-2 transition-colors"
              data-testid="nav-signin"
            >
              Sign in
            </button>
            <Button
              onClick={goSignup}
              className="rounded-full bg-white text-zinc-950 hover:bg-zinc-200 font-medium px-4 sm:px-5 h-9 shadow-lg shadow-indigo-500/20"
              data-testid="nav-get-started"
            >
              Get started
              <ArrowRight className="w-4 h-4 ml-1" />
            </Button>
          </div>
        </div>
      </header>

      {/* HERO */}
      <section className="relative pt-16 sm:pt-24 pb-20 sm:pb-28">
        <div className="max-w-7xl mx-auto px-5 sm:px-8">
          <div className="max-w-3xl">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-white/10 bg-white/5 backdrop-blur-md text-xs text-zinc-300 mb-7" data-testid="hero-eyebrow">
              <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />
              GPT-5.2 + Whisper · No video edits needed
            </div>

            <h1 className="font-heading text-4xl sm:text-6xl lg:text-7xl font-bold tracking-tight leading-[1.05]" data-testid="hero-headline">
              Turn raw clips into <span className="gradient-text">scroll-stopping</span> posts.
              <br />
              <span className="text-zinc-400">In sixty seconds.</span>
            </h1>

            <p className="mt-6 text-base sm:text-lg text-zinc-400 max-w-2xl leading-relaxed" data-testid="hero-sub">
              ClipParrot transcribes your short-form video, then writes the LinkedIn hook, the X thread, the Instagram caption, the YouTube description — in your voice, on every platform, in one click.
            </p>

            <div className="mt-8 flex flex-col sm:flex-row gap-3">
              <Button
                onClick={goSignup}
                className="h-12 px-6 rounded-full bg-primary hover:bg-primary/90 text-base font-medium shadow-xl shadow-indigo-500/30 transition-all hover:scale-[1.02]"
                data-testid="hero-cta-primary"
              >
                Start free — 50 credits
                <ArrowRight className="w-4 h-4 ml-1" />
              </Button>
              <a
                href="#how-it-works"
                onClick={(e) => { e.preventDefault(); setDemoOpen(true); }}
                className="h-12 px-6 rounded-full border border-white/10 hover:border-white/30 bg-white/5 hover:bg-white/10 text-zinc-200 inline-flex items-center justify-center gap-2 transition-colors text-base cursor-pointer"
                data-testid="hero-cta-secondary"
              >
                <Play className="w-4 h-4" />
                Watch 30-sec demo
              </a>
            </div>

            <div className="mt-7 flex items-center gap-4 text-xs text-zinc-500" data-testid="hero-social-proof">
              <div className="flex -space-x-2">
                {[1, 2, 3, 4].map((i) => (
                  <div key={i} className="w-7 h-7 rounded-full border-2 border-zinc-950 bg-gradient-to-br from-indigo-500 to-purple-600" style={{ filter: `hue-rotate(${i * 40}deg)` }} />
                ))}
              </div>
              <div className="flex items-center gap-1.5">
                <div className="flex">
                  {Array.from({ length: 5 }).map((_, i) => (
                    <Star key={i} className="w-3.5 h-3.5 fill-yellow-400 text-yellow-400" />
                  ))}
                </div>
                <span>Loved by 2,400+ creators & teams</span>
              </div>
            </div>
          </div>

          {/* Hero visual */}
          <div className="mt-16 sm:mt-20 relative">
            <div className="absolute -inset-6 bg-gradient-to-tr from-indigo-500/20 via-purple-500/10 to-transparent rounded-3xl blur-2xl" />
            <button
              onClick={() => setDemoOpen(true)}
              className="relative w-full text-left group"
              aria-label="Watch 30 second demo"
              data-testid="hero-preview-trigger"
            >
              <HeroDashboardMock />
              {/* Play overlay */}
              <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                <div className="w-20 h-20 rounded-full bg-white/95 text-zinc-950 flex items-center justify-center shadow-2xl shadow-indigo-500/40 group-hover:scale-110 transition-transform duration-300">
                  <Play className="w-7 h-7 ml-1 fill-zinc-950" />
                </div>
              </div>
              <div className="absolute bottom-4 left-1/2 -translate-x-1/2 px-3 py-1.5 rounded-full bg-zinc-950/80 backdrop-blur-md border border-white/10 text-xs text-zinc-200 pointer-events-none">
                ▶ 30-sec product tour
              </div>
            </button>
          </div>
        </div>
      </section>

      {/* LOGO STRIP */}
      <section className="border-y border-white/5 bg-zinc-950/40">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 py-8">
          <p className="text-center text-[11px] uppercase tracking-[0.2em] text-zinc-500 mb-5">
            Powering content teams shipping on every platform
          </p>
          <div className="flex flex-wrap items-center justify-center gap-x-10 gap-y-4 text-zinc-500">
            {[
              { Icon: Linkedin, name: 'LinkedIn' },
              { Icon: Twitter, name: 'X / Twitter' },
              { Icon: Instagram, name: 'Instagram' },
              { Icon: Youtube, name: 'YouTube' },
              { Icon: Music2, name: 'TikTok' },
            ].map(({ Icon, name }) => (
              <div key={name} className="flex items-center gap-2 text-sm">
                <Icon className="w-4 h-4" />
                <span>{name}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* PROBLEM */}
      <section className="py-20 sm:py-28">
        <div className="max-w-7xl mx-auto px-5 sm:px-8">
          <div className="max-w-2xl mb-14">
            <div className="text-xs uppercase tracking-[0.2em] text-indigo-300 mb-4">The problem</div>
            <h2 className="font-heading text-3xl sm:text-5xl font-bold leading-tight">
              You already have the clip. <br />
              <span className="text-zinc-500">You&apos;re stuck on the caption.</span>
            </h2>
          </div>

          <div className="grid md:grid-cols-3 gap-5">
            {[
              {
                title: 'Blank-page tax',
                body: 'Every clip you post costs 20 minutes of "how do I word this?" — multiplied by every platform you publish to.',
              },
              {
                title: 'Voice drift',
                body: 'Generic AI captions don\'t sound like you. Your audience can smell it. Engagement quietly dies.',
              },
              {
                title: 'Lost in folders',
                body: 'Raw clips live in Dropbox. Transcripts live in Notion. Captions live nowhere. Posting gets put off — again.',
              },
            ].map((p) => (
              <div key={p.title} className="rounded-2xl border border-white/5 bg-gradient-to-b from-zinc-900/80 to-zinc-950/40 p-6 hover:border-white/15 transition-colors">
                <div className="w-9 h-9 rounded-lg bg-red-500/10 text-red-300 flex items-center justify-center mb-4 border border-red-500/20">
                  <Clock className="w-4 h-4" />
                </div>
                <h3 className="font-heading text-lg text-zinc-100 mb-2">{p.title}</h3>
                <p className="text-sm text-zinc-400 leading-relaxed">{p.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section id="how-it-works" className="py-20 sm:py-28 border-y border-white/5 bg-zinc-950/40">
        <div className="max-w-7xl mx-auto px-5 sm:px-8">
          <div className="max-w-2xl mb-14">
            <div className="text-xs uppercase tracking-[0.2em] text-indigo-300 mb-4">How it works</div>
            <h2 className="font-heading text-3xl sm:text-5xl font-bold leading-tight">
              Three steps. <span className="text-zinc-500">One scroll-stopping post.</span>
            </h2>
          </div>

          <div className="grid md:grid-cols-3 gap-5 relative">
            {[
              {
                step: '01',
                Icon: Upload,
                title: 'Drop your clip',
                body: 'Drag in a single video or batch upload. We handle files up to 500MB and stream straight into secure object storage.',
              },
              {
                step: '02',
                Icon: Mic,
                title: 'We transcribe it',
                body: 'Whisper transcribes every word with timestamps. Multilingual. Punchy phrases get surfaced as quote-ready hooks.',
              },
              {
                step: '03',
                Icon: Sparkles,
                title: 'Pick a preset, publish',
                body: 'Choose a preset (or build one in plain English) and ClipParrot writes platform-specific copy in your voice.',
              },
            ].map((s) => (
              <div key={s.step} className="relative rounded-2xl border border-white/5 bg-zinc-900/40 p-7 hover:border-indigo-500/30 transition-colors group">
                <div className="font-display text-5xl text-zinc-700 group-hover:text-indigo-400 transition-colors mb-4">{s.step}</div>
                <s.Icon className="w-6 h-6 text-indigo-400 mb-3" strokeWidth={1.5} />
                <h3 className="font-heading text-xl text-zinc-100 mb-2">{s.title}</h3>
                <p className="text-sm text-zinc-400 leading-relaxed">{s.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FEATURES (bento with embedded UI) */}
      <section id="features" className="py-20 sm:py-28">
        <div className="max-w-7xl mx-auto px-5 sm:px-8">
          <div className="max-w-2xl mb-14">
            <div className="text-xs uppercase tracking-[0.2em] text-indigo-300 mb-4">Features</div>
            <h2 className="font-heading text-3xl sm:text-5xl font-bold leading-tight">
              Built like the tool you wish you had <br />
              <span className="text-zinc-500">when you went viral last time.</span>
            </h2>
          </div>

          {/* Feature 1 — Preset Builder */}
          <div className="grid lg:grid-cols-12 gap-8 lg:gap-12 items-center mb-20">
            <div className="lg:col-span-5 order-2 lg:order-1">
              <div className="inline-flex items-center gap-2 text-xs px-2.5 py-1 rounded-full bg-indigo-500/10 text-indigo-300 border border-indigo-500/30 mb-4">
                <Wand2 className="w-3.5 h-3.5" /> AI Preset Builder
              </div>
              <h3 className="font-heading text-2xl sm:text-4xl font-bold mb-4 leading-tight">
                Describe your voice. Save it as a preset. Reuse forever.
              </h3>
              <p className="text-zinc-400 leading-relaxed mb-6">
                Type "punchy LinkedIn hook, founder voice, contrarian, no emojis" and ClipParrot turns that into a reusable preset with tone, length, hashtags, CTA style and platform locked in. Or browse community presets and remix them.
              </p>
              <ul className="space-y-2.5 text-sm text-zinc-300">
                {[
                  'Natural-language preset creation',
                  'Per-platform overrides (LinkedIn, X, IG, YT)',
                  'Community presets you can remix',
                  'Version history so you never lose a vibe',
                ].map((f) => (
                  <li key={f} className="flex items-start gap-2">
                    <CheckCircle2 className="w-4 h-4 text-emerald-400 mt-0.5 shrink-0" />
                    {f}
                  </li>
                ))}
              </ul>
            </div>
            <div className="lg:col-span-7 order-1 lg:order-2">
              <PresetBuilderMock />
            </div>
          </div>

          {/* Feature 2 — Pipeline */}
          <div className="grid lg:grid-cols-12 gap-8 lg:gap-12 items-center mb-20">
            <div className="lg:col-span-7">
              <PipelineMock />
            </div>
            <div className="lg:col-span-5">
              <div className="inline-flex items-center gap-2 text-xs px-2.5 py-1 rounded-full bg-purple-500/10 text-purple-300 border border-purple-500/30 mb-4">
                <Workflow className="w-3.5 h-3.5" /> Pipeline view
              </div>
              <h3 className="font-heading text-2xl sm:text-4xl font-bold mb-4 leading-tight">
                Every clip, every stage, one board.
              </h3>
              <p className="text-zinc-400 leading-relaxed mb-6">
                A Kanban-style pipeline shows uploaded, transcribing, generating, and ready clips at a glance. Click any card to jump in. No more "wait, where did I leave that one?"
              </p>
              <ul className="space-y-2.5 text-sm text-zinc-300">
                {[
                  'Live status: uploading → transcribing → ready',
                  'Batch upload entire folders',
                  'Retry failed clips with one click',
                ].map((f) => (
                  <li key={f} className="flex items-start gap-2">
                    <CheckCircle2 className="w-4 h-4 text-emerald-400 mt-0.5 shrink-0" />
                    {f}
                  </li>
                ))}
              </ul>
            </div>
          </div>

          {/* Feature 3 — Library */}
          <div className="grid lg:grid-cols-12 gap-8 lg:gap-12 items-center mb-20">
            <div className="lg:col-span-5 order-2 lg:order-1">
              <div className="inline-flex items-center gap-2 text-xs px-2.5 py-1 rounded-full bg-emerald-500/10 text-emerald-300 border border-emerald-500/30 mb-4">
                <Library className="w-3.5 h-3.5" /> Library & folders
              </div>
              <h3 className="font-heading text-2xl sm:text-4xl font-bold mb-4 leading-tight">
                Your clip vault. Searchable, sortable, tagged.
              </h3>
              <p className="text-zinc-400 leading-relaxed mb-6">
                Group by launch, campaign, or speaker. Filter by status. Export transcripts as .docx. Everything lives in secure cloud storage — survives container restarts, follows you across devices.
              </p>
              <ul className="space-y-2.5 text-sm text-zinc-300">
                {[
                  'Folders, tabs and instant search',
                  '.docx transcript export',
                  'Secure object storage (up to 500MB / clip)',
                ].map((f) => (
                  <li key={f} className="flex items-start gap-2">
                    <CheckCircle2 className="w-4 h-4 text-emerald-400 mt-0.5 shrink-0" />
                    {f}
                  </li>
                ))}
              </ul>
            </div>
            <div className="lg:col-span-7 order-1 lg:order-2">
              <LibraryMock />
            </div>
          </div>

          {/* Feature 4 — Multi-platform output */}
          <div className="grid lg:grid-cols-12 gap-8 lg:gap-12 items-center">
            <div className="lg:col-span-7">
              <CopyOutputMock />
            </div>
            <div className="lg:col-span-5">
              <div className="inline-flex items-center gap-2 text-xs px-2.5 py-1 rounded-full bg-fuchsia-500/10 text-fuchsia-300 border border-fuchsia-500/30 mb-4">
                <Hash className="w-3.5 h-3.5" /> Multi-platform copy
              </div>
              <h3 className="font-heading text-2xl sm:text-4xl font-bold mb-4 leading-tight">
                One clip. Four platforms. Zero context-switching.
              </h3>
              <p className="text-zinc-400 leading-relaxed mb-6">
                LinkedIn hook, X thread, Instagram caption, YouTube description — generated together, optimized separately. Each respects the platform&apos;s rhythm, character limits and audience expectations.
              </p>
              <ul className="space-y-2.5 text-sm text-zinc-300">
                {[
                  'Platform-aware character limits & rhythm',
                  'One-click regenerate with new angle',
                  'Copy buttons + history of every variant',
                ].map((f) => (
                  <li key={f} className="flex items-start gap-2">
                    <CheckCircle2 className="w-4 h-4 text-emerald-400 mt-0.5 shrink-0" />
                    {f}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* USE CASES */}
      <section id="use-cases" className="py-20 sm:py-28 border-y border-white/5 bg-zinc-950/40">
        <div className="max-w-7xl mx-auto px-5 sm:px-8">
          <div className="max-w-2xl mb-14">
            <div className="text-xs uppercase tracking-[0.2em] text-indigo-300 mb-4">Built for</div>
            <h2 className="font-heading text-3xl sm:text-5xl font-bold leading-tight">
              People who ship clips. <br />
              <span className="text-zinc-500">Not people who babysit captions.</span>
            </h2>
          </div>
          <div className="grid md:grid-cols-3 gap-5">
            {[
              {
                Icon: Rocket,
                tag: 'Founders & solo creators',
                title: 'Post daily without burning out',
                body: 'Record once a week. Cut 10 clips. ClipParrot turns each into a week of posts that actually sound like you.',
              },
              {
                Icon: Brain,
                tag: 'Marketing teams',
                title: 'Repurpose your webinar deck',
                body: 'Upload a 60-min webinar. Get 30 LinkedIn posts, 10 X threads, and a YouTube Shorts description set — same afternoon.',
              },
              {
                Icon: Layers,
                tag: 'Agencies',
                title: 'Brand-voice presets per client',
                body: 'Workspaces, saved presets, and audit logs so your team posts on-brand for 12 different clients without mixing voices.',
              },
            ].map((u) => (
              <div key={u.tag} className="group rounded-2xl border border-white/5 bg-zinc-900/40 p-7 hover:border-indigo-500/30 hover:bg-zinc-900/70 transition-all">
                <u.Icon className="w-7 h-7 text-indigo-400 mb-5" strokeWidth={1.5} />
                <div className="text-xs uppercase tracking-[0.15em] text-zinc-500 mb-2">{u.tag}</div>
                <h3 className="font-heading text-xl text-zinc-100 mb-2">{u.title}</h3>
                <p className="text-sm text-zinc-400 leading-relaxed">{u.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* TESTIMONIAL */}
      <section className="py-20 sm:py-28">
        <div className="max-w-4xl mx-auto px-5 sm:px-8 text-center">
          <Quote className="w-10 h-10 text-indigo-400/60 mx-auto mb-6" strokeWidth={1.5} />
          <p className="font-display text-2xl sm:text-4xl italic text-zinc-100 leading-snug mb-8">
            "I went from posting twice a week to twice a day. The presets sound more like me than the captions I used to write at midnight."
          </p>
          <div className="flex items-center justify-center gap-3">
            <div className="w-11 h-11 rounded-full bg-gradient-to-br from-indigo-500 to-purple-600" />
            <div className="text-left">
              <div className="text-sm font-medium text-zinc-100">Maya R.</div>
              <div className="text-xs text-zinc-500">Founder · Linkedin: 38k followers</div>
            </div>
          </div>
        </div>
      </section>

      {/* PRICING TEASER */}
      <section id="pricing" className="py-20 sm:py-28 border-y border-white/5 bg-zinc-950/40">
        <div className="max-w-7xl mx-auto px-5 sm:px-8">
          <div className="text-center max-w-2xl mx-auto mb-14">
            <div className="text-xs uppercase tracking-[0.2em] text-indigo-300 mb-4">Pricing</div>
            <h2 className="font-heading text-3xl sm:text-5xl font-bold leading-tight">
              Start free. <span className="text-zinc-500">Upgrade when you go viral.</span>
            </h2>
            <p className="text-zinc-400 mt-4">Every plan includes Whisper transcription, multi-platform copy, .docx export, and secure cloud storage.</p>
          </div>

          <div className="grid md:grid-cols-3 gap-5">
            {[
              {
                name: 'Free',
                price: '$0',
                tag: 'Forever',
                features: ['50 credits to start', '1 platform output per clip', 'Community presets', 'Up to 100MB / clip'],
                cta: 'Start free',
                highlight: false,
              },
              {
                name: 'Pro',
                price: '$29',
                tag: '/ month',
                features: ['500 credits / month', 'All 4 platforms per clip', 'AI Preset Builder', 'Up to 500MB / clip', 'Priority queue'],
                cta: 'Start 14-day trial',
                highlight: true,
              },
              {
                name: 'Studio',
                price: '$99',
                tag: '/ month',
                features: ['2,000 credits / month', 'Team workspaces', 'Brand voice presets', 'Audit log + RBAC', 'Dedicated support'],
                cta: 'Talk to us',
                highlight: false,
              },
            ].map((p) => (
              <div
                key={p.name}
                className={`relative rounded-2xl p-7 border transition-all ${
                  p.highlight
                    ? 'border-indigo-500/40 bg-gradient-to-b from-indigo-500/10 to-zinc-900/40 shadow-2xl shadow-indigo-500/20 scale-[1.02]'
                    : 'border-white/5 bg-zinc-900/40 hover:border-white/15'
                }`}
                data-testid={`pricing-card-${p.name.toLowerCase()}`}
              >
                {p.highlight && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2 text-[10px] uppercase tracking-[0.15em] px-3 py-1 rounded-full bg-indigo-500 text-white font-medium">
                    Most popular
                  </div>
                )}
                <div className="text-sm text-zinc-400 mb-2">{p.name}</div>
                <div className="flex items-baseline gap-1.5 mb-5">
                  <span className="font-heading text-5xl font-bold text-zinc-50">{p.price}</span>
                  <span className="text-zinc-500 text-sm">{p.tag}</span>
                </div>
                <ul className="space-y-2.5 text-sm text-zinc-300 mb-7">
                  {p.features.map((f) => (
                    <li key={f} className="flex items-start gap-2">
                      <CheckCircle2 className="w-4 h-4 text-emerald-400 mt-0.5 shrink-0" />
                      {f}
                    </li>
                  ))}
                </ul>
                <Button
                  onClick={goSignup}
                  className={`w-full h-11 rounded-full font-medium ${
                    p.highlight
                      ? 'bg-white text-zinc-950 hover:bg-zinc-200'
                      : 'bg-zinc-800 hover:bg-zinc-700 text-zinc-100'
                  }`}
                  data-testid={`pricing-cta-${p.name.toLowerCase()}`}
                >
                  {p.cta}
                </Button>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section id="faq" className="py-20 sm:py-28">
        <div className="max-w-3xl mx-auto px-5 sm:px-8">
          <div className="text-center mb-14">
            <div className="text-xs uppercase tracking-[0.2em] text-indigo-300 mb-4">FAQ</div>
            <h2 className="font-heading text-3xl sm:text-5xl font-bold leading-tight">
              Answered before you ask.
            </h2>
          </div>

          <Accordion type="single" collapsible className="space-y-3" data-testid="faq-accordion">
            {[
              {
                q: 'What video formats and lengths do you support?',
                a: 'MP4, MOV, WebM and most common formats up to 500MB per clip. Whisper handles everything from 5-second hooks to hour-long webinars — though most users get the best results with clips under 10 minutes.',
              },
              {
                q: 'Does the AI actually sound like me?',
                a: 'It learns from the presets you build. Describe your voice in plain English ("dry, founder-tone, no emojis, contrarian openers") and save it. Every generation from that preset will respect those rules. The more you remix, the better it gets.',
              },
              {
                q: 'How does the credit system work?',
                a: 'One credit = one platform output for one clip. Generating LinkedIn + X + Instagram + YouTube from a single clip uses 4 credits. Free plan starts you with 50, Pro gives 500/month, and you can top up anytime.',
              },
              {
                q: 'Is my video data private?',
                a: 'Yes. Clips are stored in encrypted object storage scoped to your account. You can delete them anytime. We never train models on your content.',
              },
              {
                q: 'Can my whole team use the same workspace?',
                a: 'Studio plan unlocks team workspaces with role-based access, shared presets, and a full audit log of who generated what — so agencies can keep client voices separated.',
              },
              {
                q: 'Can I export transcripts?',
                a: 'Every clip ships with a one-click .docx export of the full transcript, plus copy-and-paste of every generated post variant.',
              },
            ].map((f, i) => (
              <AccordionItem
                key={f.q}
                value={`item-${i}`}
                className="border border-white/5 rounded-xl bg-zinc-900/40 px-5 data-[state=open]:border-indigo-500/30 data-[state=open]:bg-zinc-900/70 transition-colors"
                data-testid={`faq-item-${i}`}
              >
                <AccordionTrigger className="text-left text-zinc-100 hover:text-white py-5 font-medium hover:no-underline">
                  {f.q}
                </AccordionTrigger>
                <AccordionContent className="text-zinc-400 leading-relaxed pb-5">
                  {f.a}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="relative py-24 sm:py-32 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-indigo-600/30 via-purple-600/20 to-fuchsia-600/30 blur-3xl" />
        <div className="relative max-w-4xl mx-auto px-5 sm:px-8 text-center">
          <h2 className="font-heading text-4xl sm:text-6xl font-bold leading-[1.05] mb-6">
            Stop guessing. <br className="sm:hidden" />
            <span className="gradient-text">Start posting.</span>
          </h2>
          <p className="text-lg text-zinc-300 mb-10 max-w-xl mx-auto">
            50 free credits the moment you sign up. No credit card. No "schedule a demo." Just upload a clip and watch.
          </p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <Button
              onClick={goSignup}
              className="h-12 px-7 rounded-full bg-white text-zinc-950 hover:bg-zinc-200 font-medium text-base shadow-2xl shadow-indigo-500/40 transition-all hover:scale-[1.02]"
              data-testid="final-cta-primary"
            >
              Create your account
              <MoveRight className="w-4 h-4 ml-1" />
            </Button>
            <button
              onClick={goSignin}
              className="h-12 px-7 rounded-full border border-white/20 hover:border-white/40 text-zinc-200 transition-colors inline-flex items-center justify-center"
              data-testid="final-cta-secondary"
            >
              I already have an account
            </button>
          </div>
          <div className="mt-8 inline-flex items-center gap-2 text-xs text-zinc-400">
            <ShieldCheck className="w-3.5 h-3.5 text-emerald-400" />
            SOC-grade storage · GDPR-friendly · Delete anytime
          </div>
        </div>
      </section>

      {/* FOOTER */}
      <footer className="border-t border-white/5 bg-zinc-950/80">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 py-12">
          <div className="grid md:grid-cols-4 gap-10">
            <div className="md:col-span-2">
              <img src="/logo-white-wide.png" alt="ClipParrot" className="h-10 object-contain -ml-1 mb-3" />
              <p className="text-sm text-zinc-500 max-w-xs leading-relaxed">
                Turn raw clips into scroll-stopping posts on every platform. Powered by GPT-5.2 and Whisper.
              </p>
            </div>
            <div>
              <div className="text-xs uppercase tracking-[0.15em] text-zinc-400 mb-3">Product</div>
              <ul className="space-y-2 text-sm text-zinc-500">
                <li><a href="#features" className="hover:text-white transition-colors">Features</a></li>
                <li><a href="#pricing" className="hover:text-white transition-colors">Pricing</a></li>
                <li><a href="#how-it-works" className="hover:text-white transition-colors">How it works</a></li>
                <li><a href="#faq" className="hover:text-white transition-colors">FAQ</a></li>
              </ul>
            </div>
            <div>
              <div className="text-xs uppercase tracking-[0.15em] text-zinc-400 mb-3">Account</div>
              <ul className="space-y-2 text-sm text-zinc-500">
                <li><Link to="/login" className="hover:text-white transition-colors">Sign in</Link></li>
                <li><Link to="/login?mode=register" className="hover:text-white transition-colors">Get started</Link></li>
              </ul>
            </div>
          </div>
          <div className="mt-12 pt-6 border-t border-white/5 flex flex-col sm:flex-row items-center justify-between gap-3 text-xs text-zinc-500">
            <div>© {new Date().getFullYear()} ClipParrot. All rights reserved.</div>
            <div className="flex items-center gap-4">
              <a href="#" className="hover:text-white transition-colors"><Twitter className="w-4 h-4" /></a>
              <a href="#" className="hover:text-white transition-colors"><Linkedin className="w-4 h-4" /></a>
              <a href="#" className="hover:text-white transition-colors"><Youtube className="w-4 h-4" /></a>
            </div>
          </div>
        </div>
      </footer>

      {/* Demo modal */}
      <DemoModal
        open={demoOpen}
        onClose={() => setDemoOpen(false)}
        onCTA={() => { setDemoOpen(false); goSignup(); }}
      />
    </div>
  );
}
